import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Store } from '@ngrx/store';
import { computeThresholdDate } from '../../../utils/date-utils';
import { isRuleStale } from '../../../utils/rule.utils';
import {
  Subscription,
  combineLatest,
  takeUntil,
  Subject,
  take,
  filter,
} from 'rxjs';
import { ChartService } from '../../services/chart.service';
import { PiDataService } from '../../services/pi-data.service';
import {
  selectRuleByUid,
  selectRulesError,
  selectRulesLoading,
} from '../../store/selectors/rules.selector';
import {
  loadRule,
  dropRule,
  dropRuleSuccess,
} from '../../store/actions/rules.actions';
import {
  loadAlarmDetailsTrendIfNeeded,
  loadAlarmDetailsTrend,
} from '../../store/actions/rule-result.actions';
import { selectAlarmDetailsTrendData } from '../../store/selectors/rule-result.selector';
import { AlarmService } from '../../services/alarm.service';
import { RuleService } from '../../services/rule.service';
import { AuthService } from '../../core/services/auth.service';

import { Rule } from '../../models/rule.model';
import { ChartData, ChartOptions } from 'chart.js';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { RuleDialogService } from '../../shared/components/modify-rule/rule-dialog.service';
import {
  getStageLabel,
  getCriticalityLabel,
} from '../../../utils/enum-label.utils';
import { CriticalityLevel } from '../../../enum/criticality-level.enum';
import { AlarmStatus } from '../../../enum/status.enum';
import { GainFormulaInstancesReadOnlyResponse } from '../../models/gain-formula-instance.model';
import { GainFormulaInstanceService } from '../../services/gain-formula-instance.service';
import { TIME_FRAME } from '../../shared/constants/filters.constants';
import { authorizedUsers } from '../../shared/constants/admin-users.constant';
import { Actions, ofType } from '@ngrx/effects';
import {
  GLOBAL_CONFIRMATION_DELETE,
  GLOBAL_TOAST,
  RULE_CONFIRMATION_DELETE,
} from '../../shared/constants/global-key.constans';
import { PiDataPointsDatasetConverter } from '../../../utils/chart/pi-datapoints-dataset-converter.utils';
import { InactiveDataPlugin } from '../../../utils/chart/inactive-data-plugin.utils';
import { RuleEvaluationDatasetConverter } from '../../../utils/chart/rule-evaluation-dataset-converter.utils';
import { DefaultChartOptions } from '../../../utils/chart/default-options.utils';
import { ThemeService } from '../../core/services/theme.service';

@Component({
  selector: 'app-alarm-details',
  templateUrl: './alarm-details.component.html',
  styleUrl: './alarm-details.component.scss',
  standalone: false,
})
export class AlarmDetailsComponent implements OnInit, OnDestroy {
  ruleUid: string = '';
  rule?: Rule;
  trendChartOptions: ChartOptions<'line'> | undefined;
  chartData: ChartData<'line'> | undefined;
  selectedPeriod: string = TIME_FRAME.THREEMONTH;
  isDeleting = false;
  acceptBtnProps: any = { label: 'Oui' };
  rejectBtnProps: any = { label: 'Non', severity: 'secondary', outlined: true };
  ruleEvaluationsDataset: ChartData<'line'> | undefined;
  interactivePiChartDatasets: ChartData<'line'> | undefined;
  chartTimePeriod: 'hour' | 'day' | 'week' | 'month' = 'day';
  timeFrames = Object.values(TIME_FRAME);
  detailsTabValue = 'description';
  infoTabValue = 'general';
  ruleEvaluationChartAdditionalPlugins: any[] = [];
  ruleEvaluationChartOptions: ChartOptions<'line'> = {};
  piPointsChartOptions: ChartOptions<'line'> = {};
  readonly GLOBAL_CONFIRMATION_DELETE = GLOBAL_CONFIRMATION_DELETE;
  readonly RULE_CONFIRMATION_DELETE = RULE_CONFIRMATION_DELETE;

  getStageLabel = getStageLabel;
  getCriticalityLabel = getCriticalityLabel;
  isRuleStale = isRuleStale;
  CriticalityLevel = CriticalityLevel;
  AlarmStatus = AlarmStatus;
  loading$ = this.store.select(selectRulesLoading);
  error$ = this.store.select(selectRulesError);
  private destroy$ = new Subject<void>();

  gainInstances?: GainFormulaInstancesReadOnlyResponse;
  gainInstancesLoading = false;
  gainInstancesError = false;

  private loadGainInstances(ruleUid: string): void {
    this.gainInstancesLoading = true;
    this.gainInstancesError = false;
    this.gainFormulaInstanceService.getByRuleReadOnly(ruleUid).subscribe({
      next: (result) => {
        this.gainInstances = result;
        this.gainInstancesLoading = false;
      },
      error: () => {
        this.gainInstancesError = true;
        this.gainInstancesLoading = false;
      },
    });
  }

  private subscriptions = new Subscription();
  today: Date = new Date();
  colorHues: string[] = ['orange', 'blue', 'red', 'green', 'teal', 'purple'];
  colorClasses = {
    [TIME_FRAME.WEEK]: 'bg-blue-500',
    [TIME_FRAME.MONTH]: 'bg-green-500',
    [TIME_FRAME.THREEMONTH]: 'bg-red-500',
  };
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store: Store,
    private piDataService: PiDataService,
    private dialogService: DialogService,
    private alarmService: AlarmService,
    private ruleService: RuleService,
    private authService: AuthService,
    private confirmationService: ConfirmationService,
    private actions$: Actions,
    private messageService: MessageService,
    private themeService: ThemeService,
    private ruleDialogService: RuleDialogService,
    private location: Location,
    private gainFormulaInstanceService: GainFormulaInstanceService,
  ) {}

  ngOnInit(): void {
    const routeSub = this.route.queryParamMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.ruleUid = id;
        this.loadRuleDetails();
        this.subscribeToDeleteRuleResults();
      }
      const tab = params.get('tab');
      if (tab) {
        this.detailsTabValue = tab;
      }
    });
    // Subscribe to theme changes to update chart options
    const themeSub = this.themeService.darkMode$.subscribe(() => {
      this.updateChartOptions();
    });

    this.subscriptions.add(routeSub);
    this.subscriptions.add(themeSub);

    this.updateChartOptions();
  }

  private updateChartOptions(): void {
    this.ruleEvaluationChartOptions = DefaultChartOptions.getOptions(
      this.chartTimePeriod,
      'MMM dd, HH:mm',
      'Valeur',
      true,
      this.selectedPeriod,
    );
    this.piPointsChartOptions = DefaultChartOptions.getOptions(
      this.chartTimePeriod,
      'MMM dd, HH:mm',
      'Valeur',
      true,
      this.selectedPeriod,
    );
  }

  loadRuleDetails(): void {
    this.store
      .select(selectRuleByUid(this.ruleUid))
      .pipe(take(1))
      .subscribe((rule) => {
        if (!rule) {
          this.store.dispatch(loadRule({ uniqueId: this.ruleUid }));
        }
      });

    this.store
      .select(selectRuleByUid(this.ruleUid))
      .pipe(
        takeUntil(this.destroy$),
        filter((r): r is Rule => !!r),
      )
      .subscribe((rule) => {
        this.rule = rule;
        this.loadGainInstances(rule.uid);
        this.loadPiInfo();
        this.loadChartData();
      });
  }

  backToFactoryPage(): void {
    this.location.back();
  }

  loadData(period: string): void {
    this.selectedPeriod = period;
    this.updateChartOptions();
    this.loadChartData();
    this.loadPiInfo();
  }

  loadPiInfo(): void {
    const thresholdDate = computeThresholdDate(this.selectedPeriod);
    const piPointValues = this.piDataService.GetPisValues(
      this.rule?.ruleToPis,
      thresholdDate.toISOString(),
      undefined,
      this.rule?.piPointInterpolationIntervalInMinutes ?? 10,
    );
    const piPointDetails$ = this.piDataService.GetPiPointsDetails(
      this.rule?.ruleToPis,
    );

    const chartDataSub = combineLatest([
      piPointValues,
      piPointDetails$,
    ]).subscribe(([piPoints, piPointDetails]) => {
      this.interactivePiChartDatasets =
        PiDataPointsDatasetConverter.getChartData(piPoints, piPointDetails);
    });

    this.subscriptions.add(chartDataSub);
  }

  subscribeToDeleteRuleResults(): void {
    this.actions$
      .pipe(ofType(dropRuleSuccess), takeUntil(this.destroy$))
      .subscribe(() => {
        this.isDeleting = false;
        this.confirmationService.close();
        this.messageService.add({
          key: GLOBAL_TOAST,
          severity: 'success',
          summary: 'Succès',
          detail: 'La suppression de la règle a été effectuée avec succès.',
        });
        this.backToFactoryPage();
      });
  }

  loadChartData(): void {
    this.store.dispatch(
      loadAlarmDetailsTrendIfNeeded({
        uid: this.ruleUid,
        range: this.selectedPeriod,
        alertThreshold: this.rule?.alertThreshold ?? 0,
        interpolationInMinutes:
          this.selectedPeriod === TIME_FRAME.SIXMONTH ? 60 : 10,
      }),
    );

    const sub = combineLatest([
      this.store.select(
        selectAlarmDetailsTrendData(this.ruleUid, this.selectedPeriod),
      ),
    ]).subscribe(([data]) => {
      if (!data) return;
      this.ruleEvaluationsDataset =
        RuleEvaluationDatasetConverter.GenerateChartData(
          this.selectedPeriod ?? TIME_FRAME.THREEMONTH,
          data,
          (this.rule?.periodInMinutes ?? 60) * 2,
          this.rule?.alertThreshold,
          this.rule?.toleranceThreshold,
          this.rule?.minAlertThreshold,
          this.rule?.minToleranceThreshold,
        );
      this.ruleEvaluationChartAdditionalPlugins = [
        InactiveDataPlugin.createInactiveBackgroundPlugin(data),
      ];
    });

    this.subscriptions.add(sub);
  }

  onChartZoom(event: { min: Date; max: Date }): void {}

  onChartReset(): void {
    this.store.dispatch(
      loadAlarmDetailsTrend({
        uid: this.ruleUid,
        range: this.selectedPeriod,
        alertThreshold: this.rule?.alertThreshold ?? 0,
        interpolationInMinutes:
          this.selectedPeriod === TIME_FRAME.SIXMONTH ? 60 : 10,
      }),
    );
  }

  onDeleteRule() {
    this.isDeleting = false;

    this.confirmationService.confirm({
      key: this.RULE_CONFIRMATION_DELETE,
      header: 'Confirmation de suppression',
      icon: 'pi pi-exclamation-triangle',
      message:
        'Êtes-vous sûr de vouloir supprimer cette règle ? Cette action ne peut pas être annulée.',
      accept: () => {
        this.confirmationService.close();
      },
      reject: () => {},
    });
  }

  handleAccept() {
    this.isDeleting = true;
    const uid = this.rule?.uid;
    if (!uid) return;
    this.store.dispatch(dropRule({ uid }));
  }

  onclickModifyRule() {
    if (!this.rule) return;

    const ref = this.ruleDialogService.openEditRuleDialog(this.rule);

    if (ref) {
      ref.onClose.subscribe((result) => {
        if (result) {
          this.loadRuleDetails();
        }
      });
    }
  }

  isAuthorizedUser(): boolean {
    const currentUser = this.authService.getUser();
    return authorizedUsers.includes(currentUser?.email ?? '');
  }

  onCloseAlert(): void {
    if (!this.rule?.latestAlarm?.uid) {
      console.error('No alarm UID available for closing');
      return;
    }

    this.confirmationService.confirm({
      message:
        'Êtes-vous sûr de vouloir fermer cette alerte ? Cette action ne peut pas être annulée.',
      header: 'Confirmation de fermeture',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.confirmationService.close();
        this.closeAlert(this.rule!.latestAlarm!.uid!);
      },
      reject: () => {
        this.confirmationService.close();
      },
    });
  }

  private closeAlert(alarmUid: string): void {
    const closeAlertSub = this.alarmService
      .UpdateAlarmStatus({
        uid: alarmUid,
        status: AlarmStatus.Disabled,
      })
      .subscribe({
        next: () => {
          this.store.dispatch(loadRule({ uniqueId: this.ruleUid }));
        },
        error: (error) => {
          console.error('Error closing alert:', error);
        },
      });

    this.subscriptions.add(closeAlertSub);
  }

  onTrackedToggle(isTracked: boolean): void {
    if (!this.rule?.uid) return;

    const trackedSub = this.ruleService
      .UpdateTracked({ uid: this.rule.uid, isTracked })
      .subscribe({
        next: () => {
          this.store.dispatch(loadRule({ uniqueId: this.ruleUid }));
        },
        error: (error) => {
          console.error('Error updating tracked state:', error);
        },
      });

    this.subscriptions.add(trackedSub);
  }

  get alertThresholdLabel(): string {
    return this.rule?.minAlertThreshold != null ? 'inférieures' : 'supérieures';
  }

  get alertThresholdRatio(): number | undefined {
    return this.rule?.minAlertThreshold != null
      ? this.rule?.evaluationsBelowMinAlertThresholdRatio
      : this.rule?.evaluationsAboveAlertThresholdRatio;
  }

  get isAlertThresholdRatioUnevaluated(): boolean {
    return (
      this.rule?.evaluationsBelowMinAlertThresholdRatio == null &&
      this.rule?.evaluationsAboveAlertThresholdRatio == null
    );
  }

  get toleranceThresholdLabel(): string {
    return this.rule?.minToleranceThreshold != null
      ? 'inférieures'
      : 'supérieures';
  }

  get toleranceThresholdRatio(): number | undefined {
    return this.rule?.minToleranceThreshold != null
      ? this.rule?.evaluationsBelowMinToleranceThresholdRatio
      : this.rule?.evaluationsAboveToleranceThresholdRatio;
  }

  get isToleranceThresholdRatioUnevaluated(): boolean {
    return (
      this.rule?.evaluationsBelowMinToleranceThresholdRatio == null &&
      this.rule?.evaluationsAboveToleranceThresholdRatio == null
    );
  }

  getColorClass(percentage?: number): string {
    if (percentage === undefined) return 'text-gray-600';

    if (percentage >= 0 && percentage <= 0.25) {
      return 'text-green-600';
    } else if (percentage >= 0.26 && percentage <= 0.5) {
      return 'text-yellow-500';
    } else if (percentage >= 0.51 && percentage <= 0.75) {
      return 'text-orange-500';
    } else if (percentage >= 0.76 && percentage <= 1.0) {
      return 'text-red-600';
    }
    return 'text-gray-600';
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
